/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
import { Col, Row } from 'antd';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { SwotAnalysis } from 'Types/CompaniesTypes';
import {
  maxMobile,
  maxWidth, midTablet, primaryFont, retina, wMBold,
} from 'Utils/utilsStyle';
import StrengthSvgIcon from 'Components/common/assets/svgIcons/StrengthSvgIcon';
import OpportunitySvgIcons from 'Components/common/assets/svgIcons/OpportunitySvgIcons';
import ThreatsSvgIcon from 'Components/common/assets/svgIcons/ThreatsSvgIcon';
import WeaknessesSvgIcon from 'Components/common/assets/svgIcons/WeaknessesSvgIcon';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import classNames from 'classnames';
import { MidTablet, scrollHandlerToggleClass } from 'Utils/UtilityFunctions';
import BulletListItem from './BulletListItem';

const Wrapper = styled.div<{ color: string }>`
 width: 100%;
 height: 100%;
 @media all and (max-width: ${midTablet}) {
   height: calc(100% - 40px);
  }
 .data-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-left: 2px;
  @media all and (max-width: ${maxMobile}) {
    padding-left: 10px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    margin-bottom: 10px;
  }
  svg {
    height: 38px;
    margin-left: 10px;
    @media all and (max-width: ${maxMobile}) {
      margin-left: 0px;
    }
  }
 }
 .pill {
  color: ${(props) => props.color};
  display: inline-block;
  font-family: ${primaryFont};
  font-weight: ${wMBold};
  font-size: 26px;
  line-height: 21px;
  padding: 0 0 0 12px;
  @media all and (max-width: ${maxWidth}) , ${retina} {
    font-size: 20px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 0 12px 0 0;
    font-size: 18px;
  }
 }
 .desk-area {
  border: 1px solid ${(props) => props.color};
  border-radius: 10px;
  padding: 16px 14px;
  height: 100%;
  @media all and (max-width: ${maxMobile}) {
    height: auto;
  }
  > div {
     .bulletItem {
      margin-bottom: 30px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        margin-bottom: 20px;
      }
     }
    &:last-child{
      .bulletItem {
       margin-bottom: 0px;
      }
    }
  }
 }
 .Strengths {
  background-color: #F5FBFF;
 }
 .Weaknesses {
  background-color: #FFFBF3;
 }
 .Opportunities {
  background-color: #FCFFF9;
 }
 .Threats {
  background-color: #FFF4F5;
 }
`;
const Container = styled.div`
  z-index: 0;
  position: relative;

  .sticky-header-swot {
    position: sticky;
    top: 144px;
    z-index: 1;
    background: #fff;
    padding: 10px 0px;
  }
  .pill-main-column {
    height: auto;
  }  
`;

type Props = {
  viewLess?: boolean
  showRefreshPopup?: boolean
  swotLoading: boolean
  isSwotTab: boolean
  swotData: SwotAnalysis[]
} & typeof defaultProps;

const defaultProps = {
  viewLess: false,
  showRefreshPopup: false,
};

const SwotPillDesk = function SwotPillDesk(props: Props) {
  const {
    swotData, swotLoading, showRefreshPopup, viewLess, isSwotTab,
  } = props;
  const isMidTab = MidTablet();

  const getIcon = (headingProp: string) => {
    if (headingProp === 'Strengths') {
      return <StrengthSvgIcon />;
    } if (headingProp === 'Weaknesses') {
      return <WeaknessesSvgIcon />;
    } if (headingProp === 'Opportunities') {
      return <OpportunitySvgIcons />;
    } if (headingProp === 'Threats') {
      return <ThreatsSvgIcon />;
    }
    return <span />;
  };

  useEffect(() => {
    if (!isMidTab) {
      scrollHandlerToggleClass('.sidebar', 'sticky-header-swot', true);
    }
  }, []);

  return (
    <Container className={classNames('swot-pill-desk-container', {
      refreshPopupContainer: showRefreshPopup,
    })}
    >
      {isMidTab ? null : (
        <div className={classNames('header-swot sidebar')}>
          <Row gutter={[16, 16]}>
            {!swotLoading
              ? swotData?.map((analysis) => (
                <Col span={24} xs={24} sm={24} md={12} lg={6}>
                  <Wrapper color={analysis?.color} className="pill-main-wrap">
                    <div className="data-header">
                      {getIcon(analysis?.heading)}
                      <span className="pill">{analysis?.heading}</span>
                    </div>
                  </Wrapper>
                </Col>
              )) : [1, 2, 3, 4].map(() => (
                <Col span={24} xs={24} sm={24} md={12} lg={6}>
                  <SkeletonCustom width="100%" height="40px" diameter="10px" />
                </Col>
              ))}
          </Row>
        </div>
      )}
      <Row gutter={[16, 16]} className="h-100">
        {!swotLoading
          ? swotData?.map((analysis) => (
            <Col span={24} xs={24} sm={24} md={12} lg={6} className="pill-main-column">
              <Wrapper color={analysis?.color} className="pill-main-wrap">
                {isMidTab ? (
                  <div className="data-header">
                    {getIcon(analysis?.heading)}
                    <span className="pill">{analysis?.heading}</span>
                  </div>
                ) : null}
                <div className={`desk-area ${analysis?.heading}`}>
                  {analysis?.data?.slice(0, viewLess
                    ? isSwotTab ? 2 : 3 : analysis?.data.length).map((d) => (
                      <BulletListItem
                        color={analysis?.color}
                        listHeading={d.heading}
                        listDescription={d.description}
                      />
                    ))}
                </div>
              </Wrapper>
            </Col>
          )) : [1, 2, 3, 4].map(() => (
            <Col span={24} xs={24} sm={24} md={12} lg={6}>
              <SkeletonCustom width="100%" height="400px" diameter="10px" logo logoSize="100px" />
            </Col>
          ))}
      </Row>
    </Container>
  );
};

SwotPillDesk.defaultProps = defaultProps;
export default SwotPillDesk;
